import Component from '@glimmer/component';
import { service } from '@ember/service';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import { concat } from '@ember/helper';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import type IntlService from 'ember-intl/services/intl';
import type RfiService from 'scaleflow/services/rfi';
import type { RfiCategoryState } from 'scaleflow/services/rfi';
import type { RfiCategoryId } from 'scaleflow/utils/rfi';
import RfiDropzone from 'scaleflow/components/rfi/dropzone';
import RfiFileList from 'scaleflow/components/rfi/file-list';
import RfiStatusPill from 'scaleflow/components/rfi/status-pill';
import ChevronDown from 'scaleflow/components/tailwind-icons/chevron-down';

export interface RfiTableSignature {
  Element: HTMLDivElement;
  Args: object;
}

interface Row extends RfiCategoryState {
  isOpen: boolean;
  title: string;
  updatedText: string;
  toggleLabel: string;
  statusLabel: string;
}

const ROW_GRID =
  'grid grid-cols-[40px_minmax(0,1fr)_110px_60px_130px_44px] items-center gap-4 px-5';

export default class RfiTable extends Component<RfiTableSignature> {
  @service declare rfi: RfiService;
  @service declare intl: IntlService;

  @tracked private expanded = new Set<RfiCategoryId>();

  rowGrid = ROW_GRID;

  get rows(): Row[] {
    return this.rfi.categories.map((category) => {
      const title = this.intl.t(`rfi.categories.${category.id}.title`);
      const isOpen = this.expanded.has(category.id);
      return {
        ...category,
        isOpen,
        title,
        updatedText: category.lastUpdated
          ? this.intl.formatDate(category.lastUpdated, {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })
          : '—',
        toggleLabel: this.intl.t(
          isOpen ? 'rfi.collapse-row' : 'rfi.expand-row',
          { category: title },
        ),
        statusLabel: this.intl.t(
          category.provided ? 'rfi.status-provided' : 'rfi.status-open',
        ),
      };
    });
  }

  @action
  toggle(id: RfiCategoryId) {
    const next = new Set(this.expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    this.expanded = next;
  }

  @action
  add(id: RfiCategoryId, files: File[]) {
    void this.rfi.add(id, files);
  }

  @action
  download(id: RfiCategoryId, fileId: string) {
    void this.rfi.download(id, fileId);
  }

  @action
  remove(id: RfiCategoryId, fileId: string) {
    void this.rfi.remove(id, fileId);
  }

  <template>
    <div
      role="table"
      aria-label={{t "rfi.table-label"}}
      class="overflow-hidden rounded-xl border border-gray-200 bg-white"
      ...attributes
    >
      <div
        role="row"
        class="{{this.rowGrid}}
          border-b border-gray-200 bg-gray-50 py-3 text-xs font-semibold uppercase tracking-wide text-gray-600"
      >
        <div role="columnheader">#</div>
        <div role="columnheader">{{t "rfi.columns.category"}}</div>
        <div role="columnheader">{{t "rfi.columns.status"}}</div>
        <div role="columnheader">{{t "rfi.columns.files"}}</div>
        <div role="columnheader">{{t "rfi.columns.updated"}}</div>
        <div role="columnheader"><span class="sr-only">{{t
              "rfi.columns.actions"
            }}</span></div>
      </div>

      {{#each this.rows as |row|}}
        <div role="rowgroup" class="border-b border-gray-200 last:border-b-0">
          <div
            role="row"
            class="{{this.rowGrid}} py-3.5 {{if row.isOpen 'bg-primary-50/40'}}"
          >
            <div role="cell">
              <span
                class="flex h-7 w-7 items-center justify-center rounded-full bg-primary-50 text-xs font-semibold text-primary-800"
              >{{row.number}}</span>
            </div>
            <div role="cell" class="flex min-w-0 flex-col gap-0.5">
              <span
                class="text-[15px] font-semibold text-gray-900"
              >{{row.title}}</span>
              <span class="text-sm text-gray-600">{{t
                  (concat "rfi.categories." row.id ".hint")
                }}</span>
            </div>
            <div role="cell">
              <RfiStatusPill
                @provided={{row.provided}}
                @label={{row.statusLabel}}
              />
            </div>
            <div role="cell" class="text-sm text-gray-700">{{row.count}}</div>
            <div
              role="cell"
              class="text-sm text-gray-600"
            >{{row.updatedText}}</div>
            <div role="cell" class="flex justify-end">
              <button
                type="button"
                class="flex h-11 w-11 items-center justify-center rounded-full border-2
                  {{if
                    row.isOpen
                    'border-primary-600 bg-primary-600 text-white'
                    'border-primary-300 bg-primary-50 text-primary-800 hover:bg-primary-100'
                  }}"
                aria-expanded={{if row.isOpen "true" "false"}}
                aria-label={{row.toggleLabel}}
                {{on "click" (fn this.toggle row.id)}}
              >
                <ChevronDown
                  @strokeWidth="2.5"
                  class="h-5 w-5 transition-transform
                    {{if row.isOpen 'rotate-180'}}"
                />
              </button>
            </div>
          </div>

          {{#if row.isOpen}}
            <div
              class="flex flex-col gap-3 bg-primary-50/40 px-5 pb-5 pl-[76px]"
            >
              {{#if row.files.length}}
                <RfiFileList
                  @files={{row.files}}
                  @onDownload={{fn this.download row.id}}
                  @onRemove={{fn this.remove row.id}}
                />
              {{/if}}
              <RfiDropzone
                @categoryTitle={{row.title}}
                @onFiles={{fn this.add row.id}}
              />
            </div>
          {{/if}}
        </div>
      {{/each}}
    </div>
  </template>
}
